import axios, { AxiosInstance, AxiosError, AxiosResponse, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios'
import { toast } from 'sonner'
import { API_URL } from '../config/api'
import { isSpecDictatedUrl, keysToCamel, keysToSnake } from './caseConvert'
import { ApiResponseValidationError, validateResponse } from './validation'
import * as zod from '../types/zod/zod.gen'
import i18n from '../i18n/config'
import { useLicenseStore } from '../stores/licenseStore'
import { useAuthStore } from '../stores/authStore'
import { usePortalAuthStore } from '../stores/portalAuthStore'

// Friendly user-facing toast for response validation failures. The
// autogenerated Zod schemas emit verbose English bound-check messages
// (e.g. "Invalid value: Expected int64 to be >= -9223372036854775808")
// which MUST NOT bubble to end-users. Raw zodIssues are routed to
// console.error for triage; the localized `errors:responseShape` string
// (resolved at call time via the i18n instance — R-6.7 E) is what humans see.

/**
 * Run the schema attached to the request config (if any) against the
 * raw wire payload BEFORE keysToCamel runs. The generated schemas in
 * `types/zod/zod.gen.ts` describe the snake_case wire format, so
 * inverting this order would produce false-positive failures on every
 * camel/snake boundary.
 *
 * On mismatch: log the raw issue list, surface a friendly toast, and
 * rethrow `ApiResponseValidationError` so call-sites can `.catch()`.
 */
function applyResponseValidation(response: AxiosResponse): void {
  const schema = response.config.__zodSchema
  if (!schema) return
  const url = response.config.url ?? '<unknown>'
  try {
    validateResponse(schema, url, response.data)
  } catch (err) {
    if (err instanceof ApiResponseValidationError) {
      console.error('[api] response validation failed', {
        url,
        issues: err.zodIssues,
      })
      toast.error(i18n.t('errors:responseShape'))
      throw err
    }
    throw err
  }
}

// Extend axios's request config types with VynaEMR-specific opt-ins.
//
// `_retry` is the existing 401 refresh-token guard.
//
// `__zodSchema` is consumed by the response interceptor: when set, the
// interceptor validates the wire payload (still snake_case at that
// point — validation runs BEFORE keysToCamel because the generated
// Zod schemas in `types/zod/zod.gen.ts` describe the snake_case
// wire format) against this schema. On mismatch the interceptor
// throws `ApiResponseValidationError` and surfaces a friendly toast.
declare module 'axios' {
  export interface InternalAxiosRequestConfig {
    _retry?: boolean
    /**
     * Validate the response payload against this schema BEFORE
     * keysToCamel runs. Throws `ApiResponseValidationError` on
     * mismatch.
     */
    __zodSchema?: import('zod').ZodType
    /** When true, the error interceptor skips its generic toast for a 409
     *  response, leaving the caller to surface the conflict itself (e.g. the
     *  medication safety-review dialog). Other statuses still toast. */
    __suppressConflictToast?: boolean
    /** When true, the error interceptor raises NO toast at all — for callers
     *  with their own specific `onError` toast, where the generic one would
     *  double-toast every failure. License-posture handling still runs. */
    __suppressErrorToast?: boolean
  }
  export interface AxiosRequestConfig {
    /**
     * Validate the response payload against this schema BEFORE
     * keysToCamel runs. Throws `ApiResponseValidationError` on
     * mismatch.
     */
    __zodSchema?: import('zod').ZodType
    /** When true, the error interceptor skips its generic toast for a 409
     *  response, leaving the caller to surface the conflict itself (e.g. the
     *  medication safety-review dialog). Other statuses still toast. */
    __suppressConflictToast?: boolean
    /** When true, the error interceptor raises NO toast at all — for callers
     *  with their own specific `onError` toast, where the generic one would
     *  double-toast every failure. License-posture handling still runs. */
    __suppressErrorToast?: boolean
  }
}

// Create axios instance
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor:
//   1. Attach the bearer token from localStorage.
//   2. Stamp `Accept-Language: <i18next.language>` so the gateway's
//      locale middleware (R-6.5 Phase 0) can pick the right error-
//      message bundle and localise FHIR OperationOutcome bodies.
//      authStore + LanguageSwitcher keep `i18n.language` in sync with
//      the user's choice, so reading it here is always current.
//   3. Convert request bodies + query params from camelCase
//      (TypeScript convention) to snake_case (Rust + Postgres
//      convention) for routes whose wire format is owned by
//      VynaEMR. Spec-dictated routes (FHIR, CDS Hooks,
//      OAuth, JWKS) bypass conversion — see
//      `isSpecDictatedUrl` for the list.
//   4. Attach `X-Facility-Id` from the working-facility picker so the
//      SaaS license arm can attribute the request to its org (premium
//      gating + the license banner). Omitted while nothing is picked:
//      the clinical core still allows those, but premium modules
//      (billing, analytics) answer 403 FACILITY_REQUIRED — the toast
//      tells the user to pick a facility. Self-managed gateways ignore
//      the header entirely.
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('access_token')
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // i18n.language may be undefined during the brief window before
    // i18next.init() resolves; in that case fall back to 'en' so the
    // header is always present and the backend never has to guess.
    if (config.headers) {
      config.headers['Accept-Language'] = i18n.language || 'en'
    }

    const facilityId = useAuthStore.getState().selectedFacilityId
    // S5-043: 0 is never a real facility (DB serials start at 1) — sessions
    // that persisted the old Number('')===0 bug omit the header like null
    // instead of 403ing premium modules until the user re-picks.
    if (facilityId !== null && facilityId > 0 && config.headers) {
      config.headers['X-Facility-Id'] = String(facilityId)
    }

    if (!isSpecDictatedUrl(config.url)) {
      if (config.data && typeof config.data === 'object') {
        config.data = keysToSnake(config.data)
      }
      if (config.params && typeof config.params === 'object') {
        config.params = keysToSnake(config.params)
      }
      // S5-105: keys embedded in `config.url` itself ('/x?patientId=1') are
      // NOT converted — only `config.data` and `config.params` are walked.
      // Callers must pass query keys via `config.params`, never by
      // interpolating them into the URL: a camelCase key in the URL string
      // would reach the gateway unconverted while looking identical to
      // converted call sites.
    }

    return config
  },
  (error: AxiosError) => {
    return Promise.reject(error)
  }
)

// Response interceptor:
//   1. Convert response bodies from snake_case (wire) to
//      camelCase (TypeScript) for non-spec routes — same
//      opt-out as the request side so a single route either
//      converts on both legs or neither.
//   2. Handle 401 with a refresh-token retry.
//   3. Surface every other failure through `handleApiError`
//      as a toast.

// Single-flight session refresh, shared by the 401 interceptor below and
// the session-timeout "extend session" action. The gateway ROTATES refresh
// tokens — the predecessor is blacklisted on use and the response carries
// the replacement — so this persists BOTH tokens, and concurrent callers
// share one POST: two simultaneous refreshes with the same token would
// otherwise race, the loser presenting a blacklisted token and forcing a
// logout from a healthy session.
let inflightRefresh: Promise<{ accessToken: string; refreshToken: string }> | null = null

export function refreshSessionTokens(
  oldRefreshToken: string,
): Promise<{ accessToken: string; refreshToken: string }> {
  if (!inflightRefresh) {
    // Try to refresh token. The gateway reads the token from the JSON
    // body (`refresh_token`), NOT the Authorization header — sending it
    // only in the header made every refresh 422, forcing a hard logout at
    // access-token expiry. Raw `axios` (not `apiClient`) skips the
    // camelCase→snake_case request interceptor, so send snake_case here.
    const attempt = axios
      .post(`${API_URL}/auth/refresh`, { refresh_token: oldRefreshToken })
      .then((response) => {
        const { access_token, refresh_token } = response.data
        localStorage.setItem('access_token', access_token)
        // The rotated replacement — persisting only the access token
        // reuses the blacklisted predecessor at the next expiry and the
        // session dies there.
        if (refresh_token) localStorage.setItem('refresh_token', refresh_token)
        // The session-timeout hook and WS reconnects read the token from
        // the auth store, not localStorage — sync both or the session
        // still expires on the old token's schedule.
        useAuthStore.getState().setTokens(access_token, refresh_token ?? oldRefreshToken)
        return {
          accessToken: access_token as string,
          refreshToken: (refresh_token ?? oldRefreshToken) as string,
        }
      })
    inflightRefresh = attempt
    // Release the slot once settled so the NEXT expiry starts a fresh
    // refresh. Both handlers swallow — the shared promise itself still
    // rejects to its awaiters.
    attempt.then(
      () => {
        inflightRefresh = null
      },
      () => {
        inflightRefresh = null
      },
    )
  }
  return inflightRefresh
}

// Pre-auth credential endpoints: they MINT the session, so a 401 from one
// is the answer (wrong password, mfa_required, bad SSO code) — never a
// signal to refresh. `endsWith` because config.url may be relative
// ('/auth/login') or absolute depending on the call site.
const PRE_AUTH_URLS = [
  '/auth/login',
  '/auth/ldap/login',
  '/auth/sso/callback',
  '/auth/sso/saml/exchange',
]

function isPreAuthUrl(url: string | undefined): boolean {
  // S5-107: strip the query string before matching (the S4-014 cut applied to
  // the sibling predicate) — a future '/auth/login?next=...' caller would
  // otherwise route a credential 401 into the refresh flow.
  if (!url) return false
  const path = url.split('?', 1)[0]
  return PRE_AUTH_URLS.some((suffix) => path.endsWith(suffix))
}

apiClient.interceptors.response.use(
  (response) => {
    // Validate FIRST — schemas are snake_case (wire format).
    // keysToCamel runs after, only on success.
    applyResponseValidation(response)
    if (!isSpecDictatedUrl(response.config.url)) {
      if (response.data && typeof response.data === 'object') {
        response.data = keysToCamel(response.data)
      }
    }
    // License posture rides the response headers (`x-license-status:
    // valid|expiring:N|evaluation:N|grace:N on the file path,
    // saas:ok|unkeyed|unverifiable on attributed SaaS responses); the
    // LicenseBanner reads it from the store without an extra round-trip.
    useLicenseStore.getState().setFromHeader(response.headers?.['x-license-status'])
    return response
  },
  async (error: AxiosError) => {
    const originalRequest = error.config

    // Handle 401 Unauthorized — except on the pre-auth credential
    // endpoints. Those MINT the session, so no refresh can save them: a
    // stale refresh_token from an earlier session would otherwise turn a
    // wrong-password / mfa_required 401 into a refresh attempt, and a
    // failed refresh reloads to /login carrying the refresh error instead
    // of the mfa_required challenge the Login page switches on.
    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !isPreAuthUrl(originalRequest.url)
    ) {
      originalRequest._retry = true

      const refreshToken = localStorage.getItem('refresh_token')
      if (refreshToken) {
        try {
          const { accessToken } = await refreshSessionTokens(refreshToken)

          // Retry original request with new token
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${accessToken}`
          }
          return apiClient(originalRequest)
        } catch (refreshError) {
          // Refresh failed, logout user
          localStorage.removeItem('access_token')
          localStorage.removeItem('refresh_token')
          useAuthStore.getState().logout()
          window.location.href = '/login'
          return Promise.reject(refreshError)
        }
      }
    }

    // Handle other errors
    handleApiError(error)
    return Promise.reject(error)
  }
)

// Identical interceptor toasts are deduplicated inside this window. A degraded
// backend otherwise produces one toast per polling query per interval on
// polling pages, burying the actionable message under a stack of identical
// ones. The window is deliberately longer than the polling intervals (15-60s)
// so a sustained outage toasts once, not once per refresh.
const TOAST_DEDUPE_WINDOW_MS = 60_000
// S5-099: the 422 dedupe key embeds the server message, so distinct validation
// texts are distinct keys — without a cap the map grows for the life of the
// page on a noisy form surface. Past the cap the oldest entry is evicted
// (Map preserves insertion order, so the first key is the oldest).
const TOAST_DEDUPE_MAX_KEYS = 200
const recentErrorToasts = new Map<string, number>()

/**
 * True when an identical interceptor toast already fired inside the dedupe
 * window — the caller should stay silent. `nowMs` is injectable for tests.
 */
export function shouldSuppressDuplicateToast(key: string, nowMs: number = Date.now()): boolean {
  const last = recentErrorToasts.get(key)
  if (last !== undefined && nowMs - last < TOAST_DEDUPE_WINDOW_MS) return true
  if (!recentErrorToasts.has(key) && recentErrorToasts.size >= TOAST_DEDUPE_MAX_KEYS) {
    const oldest = recentErrorToasts.keys().next().value
    if (oldest !== undefined) recentErrorToasts.delete(oldest)
  }
  recentErrorToasts.set(key, nowMs)
  return false
}

/** Test-only reset for the toast dedupe map. */
export function __test_resetToastThrottle(): void {
  recentErrorToasts.clear()
}

function toastErrorDeduped(key: string, message: string): void {
  if (!shouldSuppressDuplicateToast(key)) toast.error(message)
}

// Error handler
function handleApiError(error: AxiosError) {
  if (error.response) {
    // Server responded with error
    const { status, data } = error.response
    // Terminal file-key denials carry no `x-license-status` header (the
    // middleware refuses before the handler runs), so the banner learns the
    // posture from the denial code instead.
    const code = data && typeof data === 'object' && 'code' in data
      ? (data as { code?: unknown }).code
      : undefined
    if (code === 'FILE_LICENSE_EXPIRED') useLicenseStore.getState().setTerminal('expired')
    else if (code === 'FILE_LICENSE_INVALID') useLicenseStore.getState().setTerminal('invalid')

    // Callers with their own specific onError toast opt out here so one failed
    // mutation does not produce two toasts (generic interceptor + page).
    // License-posture handling above still runs.
    if (error.config?.__suppressErrorToast) return

    // R-6.7 E: toast strings localize via the i18n INSTANCE (non-component
    // module → not the useTranslation hook), resolved at call time so they
    // follow the current language.
    //
    // Dedupe keys include the RESOLVED message: only a byte-identical toast is
    // a repeat. The same status in another language, or a 422 carrying a new
    // server message, is new information and must show.
    const deduped = (statusKey: string, message: string) =>
      toastErrorDeduped(`${statusKey}:${message.slice(0, 120)}`, message)
    switch (status) {
      case 400:
        deduped('http:400', i18n.t('errors:badRequest'))
        break
      case 403:
        // SaaS premium modules deny unattributed callers (no X-Facility-Id)
        // with FACILITY_REQUIRED instead of serving unlicensed: the fix is
        // picking a facility in the AppShell header, not asking for access.
        if (code === 'FACILITY_REQUIRED') deduped('http:403:FACILITY_REQUIRED', i18n.t('errors:facilityRequired'))
        else deduped('http:403', i18n.t('errors:forbidden'))
        break
      case 404:
        deduped('http:404', i18n.t('errors:notFound'))
        break
      case 409:
        if (!error.config?.__suppressConflictToast) deduped('http:409', i18n.t('errors:conflict'))
        break
      case 422: {
        // Validation error. The 422 branch is where the two halves of "Both"
        // meet: when the backend sends a (server-localized) `{ error: { message } }`
        // we surface IT; otherwise fall back to the localized FE string.
        const serverMessage =
          data && typeof data === 'object' && 'error' in data
            ? (data as { error: { message: string } }).error.message
            : ''
        deduped('http:422', serverMessage || i18n.t('errors:validation'))
        break
      }
      case 429:
        deduped('http:429', i18n.t('errors:tooManyRequests'))
        break
      case 500:
        deduped('http:500', i18n.t('errors:serverError'))
        break
      default:
        deduped(`http:${status}`, i18n.t('errors:unexpected'))
    }
  } else if (error.request) {
    // Request made but no response
    if (!error.config?.__suppressErrorToast) {
      const message = i18n.t('errors:network')
      toastErrorDeduped(`network:${message.slice(0, 120)}`, message)
    }
  } else {
    // Error in request setup
    if (!error.config?.__suppressErrorToast) {
      const message = i18n.t('errors:requestSetup')
      toastErrorDeduped(`setup:${message.slice(0, 120)}`, message)
    }
  }
}

// Portal-specific axios instance — reads JWT from portalAuthStore, not staff auth.
// Public portal endpoints (login, register) use apiClient; authenticated ones use this.
export const portalClient: AxiosInstance = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
})

portalClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  // Read token from portalAuthStore's persisted state
  try {
    const raw = localStorage.getItem('vynaemr-portal-auth')
    if (raw) {
      const parsed = JSON.parse(raw)
      const token = parsed?.state?.token
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`
      }
    }
  } catch { /* ignore parse errors */ }

  // R-6.5 Phase 0: same Accept-Language stamping as apiClient. Portal
  // users have their own preferredLanguage (PortalUserResponse) that
  // the portal layer pushes into i18n; reading i18n.language here
  // works equally well for either client.
  if (config.headers) {
    config.headers['Accept-Language'] = i18n.language || 'en'
  }

  if (!isSpecDictatedUrl(config.url)) {
    if (config.data && typeof config.data === 'object') {
      config.data = keysToSnake(config.data)
    }
    if (config.params && typeof config.params === 'object') {
      config.params = keysToSnake(config.params)
    }
    // S5-105: same embedded-query invariant as apiClient above — query keys
    // travel via `config.params`, never interpolated into `config.url`.
  }
  return config
})

portalClient.interceptors.response.use(
  (response) => {
    // Mirror apiClient: validate against __zodSchema BEFORE keysToCamel
    // so the snake_case wire-format schemas line up with the data they
    // see.
    applyResponseValidation(response)
    if (!isSpecDictatedUrl(response.config.url)) {
      if (response.data && typeof response.data === 'object') {
        response.data = keysToCamel(response.data)
      }
    }
    return response
  },
  async (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Portal token expired — drop the session and redirect to portal
      // login. The store reset comes FIRST: clearing the localStorage key
      // alone leaves isAuthenticated true with a dead token in memory
      // (stale authenticated UI, every call failing). Resetting first and
      // removing the persisted key after also keeps the key absent rather
      // than re-persisted with cleared state.
      usePortalAuthStore.getState().logout()
      localStorage.removeItem('vynaemr-portal-auth')
      if (window.location.pathname.startsWith('/portal')) {
        window.location.href = '/portal/login'
      }
    }
    handleApiError(error)
    return Promise.reject(error)
  }
)

// API helper functions with proper TypeScript types
import type {
  DemographicValueSetEntry,
  MedicationDispenseResponse,
  ExportKickoffParams,
  PaginatedResponse,
  Patient,
  PatientSummary,
  EncounterDiagnosis,
  EncounterTransferHistory,
  PatientSearchParams,
  MyPatientEntry,
  MyPatientsParams,
  CreatePatientRequest,
  UpdatePatientRequest,
  EncounterResponse,
  EncounterSearchParams,
  BedOccupancyResponse,
  FacilityResponse,
  CreateEncounterRequest,
  UpdateEncounterRequest,
  DischargeEncounterRequest,
  AddDiagnosisRequest,
  OrderResponse,
  OrderWithCdsResponse,
  OrderCatalogItemResponse,
  OrderSearchParams,
  CreateOrderRequest,
  CancelOrderRequest,
  ResultResponse,
  ResultSearchParams,
  CreateResultRequest,
  MedicationResponse,
  MedicationWithCdsResponse,
  MedicationSearchParams,
  CreateMedicationRequest,
  FormularyItem,
  RenalFunctionResponse,
  PregnancyStatus,
  CurrentPregnancyStatus,
  RecordPregnancyStatusRequest,
  PharmacistVerifyRequest,
  DiscontinueMedicationRequest,
  SuspendMedicationRequest,
  OverrideVerificationRequest,
  Discrepancy,
  ResolveClarificationRequest,
  AppointmentSearchParams,
  CreateAppointmentRequest,
  CheckInRequest,
  SetUserDepartmentsRequest,
  UserDepartmentAssignment,
  CreateNoteRequest,
  ClinicalNoteResponse,
  ChargeResponse,
  ClaimResponse,
  PaymentResponse,
  PaymentMethod,
  PatientAccountResponse,
  DuplicateCheckResponse,
  PatientWithDuplicatesResponse,
  ValidateIdResponse,
  MergePatientRequest,
  MergeRecordResponse,
  MergeHistoryResponse,
  DuplicateWorklistResponse,
  DismissDuplicateRequest,
  DismissDuplicateResponse,
  HandoffResponse,
  CreateHandoffRequest,
  WorklistSummary,
  CreateNursingTaskRequest,
  CreateSpecimenRequest,
  SpecimenResponse,
  SpecimenTrackResponse,
  UpdateSpecimenStatusRequest,
  DischargeChecklistResponse,
  OrderSetResponse,
  OrderSetItemResponse,
  OrderSmartPhraseResponse,
  PendingCosignOrder,
  DeadLetterEntry,
  ProjectorLagResponse,
  ThirdPartyAppResponse,
  CreateThirdPartyAppRequest,
  UpdateThirdPartyAppRequest,
  ThirdPartyLaunchRequest,
  ThirdPartyLaunchResponse,
  IntegrationStats,
  InterfaceMessageFilters,
  InterfaceMessageListResponse,
  InterfaceMessageAttempt,
  InterfaceMessageDetail,
  TimeseriesParams,
  TimeseriesResponse,
  DenialUpdateStatusRequest,
  DenialUpdateStatusResponse,
  UpdateProfileRequest,
  SwitchDepartmentResponse,
  UserRoleAssignment,
  UpdateUserRoleRequest,
  ReviewCampaign,
  ReviewCampaignDetail,
  ReviewItem,
  CreateReviewCampaignRequest,
  EscalationRuleResponse,
  UpdateEscalationRuleRequest,
  SsoProviderInfo,
  LdapDirectoryInfo,
  SsoProvider,
  CreateSsoProviderInput,
  UpdateSsoProviderInput,
  SsoDomain,
  CreateSsoDomainInput,
  SsoGroupMap,
  CreateSsoGroupMapInput,
  SsoDirectory,
  CreateSsoDirectoryInput,
  UpdateSsoDirectoryInput,
  SsoLdapMap,
  CreateSsoLdapMapInput,
  SsoDeleteKept,
  SsoTestResult,
  SsoTokenPair,
  ScimToken,
  MintScimTokenInput,
  MintedScimToken,
  UserGroup,
  UserGroupMember,
  UserGroupGrant,
  UserGroupDetail,
  CreateUserGroupInput,
  UpdateUserGroupInput,
  UserGroupDeleteResult,
  AddGroupMemberInput,
  AddGroupGrantInput,
  SetupStatusResponse,
  SetupClaimRequest,
  SetupClaimResponse,
} from '../types/api'

export const api = {
  // Authentication
  auth: {
    login: (username: string, password: string, mfaCode?: string) =>
      apiClient.post('/auth/login', { username, password, ...(mfaCode ? { mfaCode } : {}) }, {
        __zodSchema: zod.zLoginResponse,
      }),
    logout: () => apiClient.post('/auth/logout'),
    // NOTE: there is deliberately no `auth.refreshToken` here. A direct
    // POST /auth/refresh bypasses the single-flight in `refreshSessionTokens`,
    // and the gateway ROTATES refresh tokens — a concurrent refresh presents
    // a blacklisted predecessor and forces a logout from a healthy session.
    // All refreshes go through `refreshSessionTokens` (the 401 interceptor
    // and useSessionTimeout already do). Do not re-add a direct caller.
    getProfile: () =>
      apiClient.get('/auth/profile', {
        __zodSchema: zod.zGetProfileResponse,
      }),
    updateProfile: (data: UpdateProfileRequest) =>
      apiClient.put('/auth/profile', data, {
        __zodSchema: zod.zUpdateProfileResponse,
      }),
    changePassword: (data: { currentPassword: string; newPassword: string }) =>
      apiClient.put('/auth/change-password', data, {
        __zodSchema: zod.zChangePasswordResponse,
      }),
    // F3, Epic's Change Context: covering a second unit mid-shift is ordinary,
    // and logging out to do it is not. The session is re-minted server-side —
    // the caller MUST persist the returned tokens, or the next request still
    // carries the old department and stamps it on the next encounter opened.
    switchDepartment: (departmentId: number) =>
      apiClient.post<SwitchDepartmentResponse>('/auth/switch-department', { departmentId }, {
        __zodSchema: zod.zSwitchDepartmentResponse,
      }),
    mfaSetup: () => apiClient.post('/auth/mfa/setup'),
    mfaVerifySetup: (data: { code: string }) => apiClient.post('/auth/mfa/verify-setup', data),
    updateAvatar: (avatarUrl: string) =>
      apiClient.put<{ avatarUrl: string }>('/auth/profile/avatar', { avatarUrl }),
    removeAvatar: () => apiClient.delete('/auth/profile/avatar'),
    // First-run setup gate (public, no auth). The login probe fails open and
    // the setup page renders its own panels, so both suppress the generic
    // interceptor toast — a global error popup over these states would be
    // noise, not help.
    setupStatus: () =>
      apiClient.get<SetupStatusResponse>('/auth/setup', {
        __suppressErrorToast: true,
      }),
    setupClaim: (data: SetupClaimRequest) =>
      apiClient.post<SetupClaimResponse>('/auth/setup', data, {
        __suppressErrorToast: true,
      }),
  },

  // Patients
  patients: {
    // S5-041: the list wire shape is the 16-key PatientResponse summary,
    // not the full Patient detail — the old typing made three columns read
    // fields the wire never sent. Fetch `get(id)` for anything beyond it.
    list: (params?: PatientSearchParams) =>
      apiClient.get<PaginatedResponse<PatientSummary>>('/patients', {
        params,
        __zodSchema: zod.zListPatientsResponse,
      }),
    get: (id: number) =>
      apiClient.get<Patient>(`/patients/${id}`, {
        __zodSchema: zod.zGetPatientResponse,
      }),
    myPatients: (params?: MyPatientsParams) =>
      apiClient.get<PaginatedResponse<MyPatientEntry>>('/patients/my', {
        params,
        __zodSchema: zod.zListMyPatientsResponse,
      }),
    create: (data: CreatePatientRequest) =>
      apiClient.post<PatientWithDuplicatesResponse>('/patients', data, {
        __zodSchema: zod.zCreatePatientResponse,
      }),
    update: (id: number, data: UpdatePatientRequest) =>
      apiClient.put<Patient>(`/patients/${id}`, data, {
        __zodSchema: zod.zUpdatePatientResponse,
      }),
    delete: (id: number) =>
      apiClient.delete(`/patients/${id}`),
    checkDuplicate: (params: {
      fullName?: string
      // Sent separately where known: the server blocks candidates on surname,
      // and splitting a full name is ambiguous across APAC naming order.
      firstName?: string
      lastName?: string
      dateOfBirth?: string
      nationalIdNumber?: string
    }) =>
      apiClient.get<DuplicateCheckResponse>('/patients/check-duplicate', { params }),
    validateId: (data: { idType: string; idValue: string }) =>
      apiClient.post<ValidateIdResponse>('/patients/validate-id', data, {
        __zodSchema: zod.zValidateNationalIdResponse,
      }),
    /**
     * The pickable race / ethnicity values. Reference data, not patient data --
     * the endpoint requires only an authenticated caller.
     *
     * `jurisdiction` only SORTS that jurisdiction's set first; it never hides
     * another's, because a patient may be recorded under a vocabulary that is
     * not the facility's.
     *
     * Relay-only server-side: a 503 means the catalogue is unreachable, which
     * callers must distinguish from an empty list. No `__zodSchema` -- the
     * handler's utoipa annotation declares no response body, so the generator
     * emitted none. Adding one is a backend follow-up.
     */
    demographicValueSets: (params?: { kind?: 'race' | 'ethnicity'; jurisdiction?: string }) =>
      apiClient.get<DemographicValueSetEntry[]>('/patients/demographic-value-sets', { params }),
    merge: (data: MergePatientRequest) =>
      apiClient.post<MergeRecordResponse>('/patients/merge', data),
    duplicates: {
      list: (params?: { status?: string; minScore?: number; page?: number; perPage?: number }) =>
        apiClient.get<DuplicateWorklistResponse>('/patients/duplicates', {
          params,
          __zodSchema: zod.zListDuplicatesResponse,
        }),
      dismiss: (candidateId: number, data: DismissDuplicateRequest) =>
        apiClient.post<DismissDuplicateResponse>(
          `/patients/duplicates/${candidateId}/dismiss`,
          data,
          { __zodSchema: zod.zDismissDuplicateResponse }
        ),
    },
    mergeHistory: (patientId: number) =>
      apiClient.get<MergeHistoryResponse[]>(`/patients/${patientId}/merge-history`),
    /**
     * Export the patient's complete record as a portable FHIR-style Bundle.
     * Requires PATIENT_VIEW + BULK_EXPORT; the disclosure is audited server-side
     * as `PATIENT_EXPORT`.
     *
     * `responseType: 'text'` is deliberate and load-bearing. The response
     * interceptor runs `keysToCamel` only when `response.data` is an object, so
     * asking for text leaves the server's bytes untouched — and the file the
     * patient receives is then exactly what the server produced and audited.
     * Letting axios parse and re-key it would hand over a client-rewritten
     * document while the audit trail attests to a different one.
     */
    exportRecord: (patientId: number) =>
      apiClient.get<string>(`/patients/${patientId}/export`, {
        responseType: 'text',
        // S5-104: a full-record Bundle build is unbounded work — the
        // instance-default 30s aborts large records mid-flight as a generic
        // network failure (and the page's "retry" just fails again).
        timeout: 120_000,
      }),
    renalFunction: (patientId: number) =>
      apiClient.get<RenalFunctionResponse>(`/patients/${patientId}/renal-function`),
    allergies: {
      list: (patientId: number) => apiClient.get(`/patients/${patientId}/allergies`),
      add: (patientId: number, data: { allergenName: string; allergyType: string; severity: string; reaction?: string; onsetDate?: string }) => apiClient.post(`/patients/${patientId}/allergies`, data),
      remove: (patientId: number, allergyId: number) => apiClient.delete(`/patients/${patientId}/allergies/${allergyId}`),
    },
    ccdaImports: {
      list: (patientId: number) => apiClient.get(`/patients/${patientId}/ccda/imports`),
      get: (patientId: number, importId: number) =>
        apiClient.get(`/patients/${patientId}/ccda/imports/${importId}`),
      review: (patientId: number, importId: number, entryId: number, decision: 'accept' | 'reject') =>
        apiClient.post(`/patients/${patientId}/ccda/imports/${importId}/entries/${entryId}/review`, { decision }),
    },
    implantableDevices: {
      list: (patientId: number) => apiClient.get(`/patients/${patientId}/implantable-devices`),
      add: (patientId: number, data: { deviceName: string; udi?: string; deviceCode?: string; deviceCodeSystem?: string; deviceType?: string; implantDate?: string; explantDate?: string; status?: string; lotNumber?: string; serialNumber?: string; manufacturer?: string; modelNumber?: string; mriSafety?: string; notes?: string }) => apiClient.post(`/patients/${patientId}/implantable-devices`, data),
      update: (patientId: number, deviceId: number, data: { status?: string; explantDate?: string; notes?: string; mriSafety?: string }) => apiClient.put(`/patients/${patientId}/implantable-devices/${deviceId}`, data),
      remove: (patientId: number, deviceId: number) => apiClient.delete(`/patients/${patientId}/implantable-devices/${deviceId}`),
    },
    problems: {
      list: (patientId: number) => apiClient.get(`/patients/${patientId}/problems`),
      add: (patientId: number, data: { problemName: string; icdCode?: string; severity?: string; onsetDate?: string }) => apiClient.post(`/patients/${patientId}/problems`, data),
      update: (patientId: number, problemId: number, data: object) => apiClient.put(`/patients/${patientId}/problems/${problemId}`, data),
    },
    insurance: {
      list: (patientId: number) => apiClient.get(`/patients/${patientId}/insurance`),
      add: (patientId: number, data: { patientId: number; payerName: string; planName?: string; memberId: string; groupNumber?: string; effectiveDate?: string; expiryDate?: string; priority: string }) => apiClient.post(`/patients/${patientId}/insurance`, data),
    },
    immunizations: {
      list: (patientId: number) => apiClient.get(`/patients/${patientId}/immunizations`),
      add: (patientId: number, data: { patientId: number; vaccineName: string; dateAdministered: string; lotNumber?: string; site?: string; administeredBy?: string; nextDueDate?: string }) => apiClient.post(`/patients/${patientId}/immunizations`, data),
    },
    contacts: {
      list: (patientId: number) => apiClient.get(`/patients/${patientId}/contacts`),
      add: (patientId: number, data: object) => apiClient.post(`/patients/${patientId}/contacts`, data),
      update: (patientId: number, contactId: number, data: object) => apiClient.put(`/patients/${patientId}/contacts/${contactId}`, data),
      delete: (patientId: number, contactId: number) => apiClient.delete(`/patients/${patientId}/contacts/${contactId}`),
    },
    familyHistory: {
      list: (patientId: number) => apiClient.get(`/patients/${patientId}/family-history`),
      add: (patientId: number, data: object) => apiClient.post(`/patients/${patientId}/family-history`, data),
    },
    socialHistory: {
      get: (patientId: number) => apiClient.get(`/patients/${patientId}/social-history`),
      update: (patientId: number, data: object) => apiClient.put(`/patients/${patientId}/social-history`, data),
    },
    surgicalHistory: {
      list: (patientId: number) => apiClient.get(`/patients/${patientId}/surgical-history`),
      add: (patientId: number, data: object) => apiClient.post(`/patients/${patientId}/surgical-history`, data),
    },
    referrals: {
      list: (patientId: number) => apiClient.get(`/patients/${patientId}/referrals`),
      create: (patientId: number, data: object) => apiClient.post(`/patients/${patientId}/referrals`, data),
    },
    advanceDirectives: {
      list: (patientId: number) => apiClient.get(`/patients/${patientId}/advance-directives`),
      create: (patientId: number, data: object) => apiClient.post(`/patients/${patientId}/advance-directives`, data),
    },
    // Append-only screening history, newest first. `record` always inserts —
    // a correction is a new assertion, never an edit of an old one.
    pregnancyStatus: {
      list: (patientId: number) =>
        apiClient.get<PregnancyStatus[]>(`/patients/${patientId}/pregnancy-status`),
      record: (patientId: number, data: RecordPregnancyStatusRequest) =>
        apiClient.post<PregnancyStatus>(`/patients/${patientId}/pregnancy-status`, data),
      // The RESOLVED status: newest assertion with the staleness rules applied.
      // Use this for display, not `list()[0]` — the newest row is not
      // necessarily current, and only the server knows the window.
      current: (patientId: number) =>
        apiClient.get<CurrentPregnancyStatus>(
          `/patients/${patientId}/pregnancy-status/current`
        ),
    },
    identifiers: {
      list: (patientId: number) => apiClient.get(`/patients/${patientId}/identifiers`),
      add: (patientId: number, data: object) => apiClient.post(`/patients/${patientId}/identifiers`, data),
      remove: (patientId: number, identifierId: number) => apiClient.delete(`/patients/${patientId}/identifiers/${identifierId}`),
    },
    addresses: {
      list: (patientId: number) => apiClient.get(`/patients/${patientId}/addresses`),
      add: (patientId: number, data: object) => apiClient.post(`/patients/${patientId}/addresses`, data),
      update: (patientId: number, addressId: number, data: object) => apiClient.put(`/patients/${patientId}/addresses/${addressId}`, data),
      remove: (patientId: number, addressId: number) => apiClient.delete(`/patients/${patientId}/addresses/${addressId}`),
    },
    carePlans: {
      list: (patientId: number) => apiClient.get(`/patients/${patientId}/care-plans`),
      add: (patientId: number, data: object) => apiClient.post(`/patients/${patientId}/care-plans`, data),
      addGoal: (patientId: number, carePlanId: number, data: object) => apiClient.post(`/patients/${patientId}/care-plans/${carePlanId}/goals`, data),
    },
  },

  // Facilities (org directory; bedCount feeds the census capacity)
  facilities: {
    list: (params?: { page?: number; perPage?: number }) =>
      apiClient.get<PaginatedResponse<FacilityResponse>>('/facilities', {
        params,
      }),
  },

  // Locations (bed board; other location calls use apiClient directly)
  locations: {
    beds: (params?: { facilityId?: number; departmentId?: number }) =>
      apiClient.get<BedOccupancyResponse[]>('/locations/beds', {
        params,
      }),
  },

  // Encounters
  encounters: {
    list: (params?: EncounterSearchParams) =>
      apiClient.get<PaginatedResponse<EncounterResponse>>('/encounters', {
        params,
        __zodSchema: zod.zListEncountersResponse,
      }),
    get: (id: number) =>
      apiClient.get<EncounterResponse>(`/encounters/${id}`, {
        __zodSchema: zod.zGetEncounterResponse,
      }),
    create: (data: CreateEncounterRequest) =>
      apiClient.post<EncounterResponse>('/encounters', data, {
        __zodSchema: zod.zCreateEncounterResponse,
      }),
    update: (id: number, data: UpdateEncounterRequest) =>
      apiClient.put<EncounterResponse>(`/encounters/${id}`, data, {
        __zodSchema: zod.zUpdateEncounterResponse,
      }),
    delete: (id: number) =>
      apiClient.delete(`/encounters/${id}`),
    discharge: (id: number, data: DischargeEncounterRequest) =>
      apiClient.post<EncounterResponse>(`/encounters/${id}/discharge`, data, {
        __zodSchema: zod.zDischargeEncounterResponse,
      }),
    transfer: (encounterId: number, data: { toLocationId: number; transferReason: string }) =>
      apiClient.post(`/encounters/${encounterId}/transfer`, data, {
        __zodSchema: zod.zTransferEncounterResponse,
      }),
    getTransferHistory: (encounterId: number) =>
      apiClient.get<EncounterTransferHistory[]>(`/encounters/${encounterId}/transfers`, {
        __zodSchema: zod.zListTransfersResponse,
      }),
    // Vitals sub-resource (backend endpoint pending — returns 404 until implemented)
    vitals: {
      list: (encounterId: number) => apiClient.get(`/encounters/${encounterId}/vitals`),
      record: (encounterId: number, data: object) => apiClient.post(`/encounters/${encounterId}/vitals`, data),
    },
    // Intake/Output sub-resource (backend endpoint pending — returns 404 until implemented)
    io: {
      list: (encounterId: number) => apiClient.get(`/encounters/${encounterId}/io`),
      add: (encounterId: number, data: object) => apiClient.post(`/encounters/${encounterId}/io`, data),
    },
    // Diagnoses sub-resource (Sweep-4 P01). S5-103: typed + zod-wired —
    // sweep-4 added the endpoints, types AND schemas but not the wiring.
    diagnoses: {
      list: (encounterId: number) =>
        apiClient.get<EncounterDiagnosis[]>(`/encounters/${encounterId}/diagnoses`, {
          __zodSchema: zod.zListDiagnosesResponse,
        }),
      add: (encounterId: number, data: AddDiagnosisRequest) =>
        apiClient.post<EncounterDiagnosis>(`/encounters/${encounterId}/diagnoses`, data, {
          __zodSchema: zod.zAddDiagnosisResponse,
        }),
    },
  },

  // Orders (CPOE)
  orders: {
    list: (params?: OrderSearchParams) =>
      apiClient.get<PaginatedResponse<OrderResponse>>('/orders', {
        params,
        __zodSchema: zod.zListOrdersResponse,
      }),
    // Curated orderable-item catalog (OrderCatalog) — the correct source for the
    // order-entry picker. Medication items carry medicationCatalogId for dosing.
    catalog: (params?: { search?: string; perPage?: number }) =>
      apiClient.get<PaginatedResponse<OrderCatalogItemResponse>>('/orders/catalog', {
        params,
      }),
    get: (id: number) =>
      apiClient.get<OrderResponse>(`/orders/${id}`, {
        __zodSchema: zod.zGetOrderResponse,
      }),
    create: (data: CreateOrderRequest) =>
      apiClient.post<OrderWithCdsResponse>('/orders', data, {
        __zodSchema: zod.zCreateOrderResponse,
      }),
    cancel: (id: number, data: CancelOrderRequest) =>
      apiClient.post<OrderResponse>(`/orders/${id}/cancel`, data, {
        __zodSchema: zod.zCancelOrderResponse,
      }),
    complete: (id: number) =>
      apiClient.post<OrderResponse>(`/orders/${id}/complete`, undefined, {
        __zodSchema: zod.zCompleteOrderResponse,
      }),
    cosign: (id: number) =>
      apiClient.post(`/orders/${id}/cosign`),
    delete: (id: number) =>
      apiClient.delete(`/orders/${id}`),
  },

  // Results
  results: {
    list: (params?: ResultSearchParams) =>
      apiClient.get<PaginatedResponse<ResultResponse>>('/results', {
        params,
        __zodSchema: zod.zListResultsResponse,
      }),
    get: (id: number) =>
      apiClient.get<ResultResponse>(`/results/${id}`, {
        __zodSchema: zod.zGetResultResponse,
      }),
    create: (data: CreateResultRequest) =>
      apiClient.post<ResultResponse>('/results', data, {
        __zodSchema: zod.zCreateResultResponse,
      }),
    // The acknowledging provider is derived server-side from the caller
    // identity — no request body.
    acknowledge: (id: number) =>
      apiClient.post<ResultResponse>(`/results/${id}/acknowledge`, undefined, {
        __zodSchema: zod.zAcknowledgeCriticalResponse,
      }),
    // Batch: one call for the visible unacknowledged list. Ineligible ids
    // are skipped server-side; the response carries what this call stamped.
    acknowledgeBatch: (resultIds: number[]) =>
      apiClient.post<{ acknowledged: ResultResponse[] }>(
        '/results/critical/acknowledge',
        { resultIds },
        { __zodSchema: zod.zBatchAcknowledgeCriticalResponse },
      ),
    delete: (id: number) =>
      apiClient.delete(`/results/${id}`),
  },

  // Medications
  medications: {
    list: (params?: MedicationSearchParams) =>
      apiClient.get<PaginatedResponse<MedicationResponse>>('/medications', {
        params,
        __zodSchema: zod.zListMedicationsResponse,
      }),
    get: (id: number) =>
      apiClient.get<MedicationResponse>(`/medications/${id}`, {
        __zodSchema: zod.zGetMedicationResponse,
      }),
    create: (data: CreateMedicationRequest) =>
      apiClient.post<MedicationWithCdsResponse>('/medications', data, {
        __zodSchema: zod.zCreateMedicationResponse,
        __suppressConflictToast: true,
      }),
    formulary: (params?: { search?: string; category?: string; perPage?: number }) =>
      apiClient.get<PaginatedResponse<FormularyItem>>('/medications/formulary', { params }),
    verify: (id: number, data: PharmacistVerifyRequest) =>
      apiClient.post<MedicationResponse>(`/medications/${id}/verify`, data, {
        __zodSchema: zod.zVerifyMedicationResponse,
      }),
    discontinue: (id: number, data: DiscontinueMedicationRequest) =>
      apiClient.post<MedicationResponse>(`/medications/${id}/discontinue`, data, {
        __zodSchema: zod.zDiscontinueMedicationResponse,
      }),
    /**
     * Hold the whole order — NPO from midnight, an anticoagulant paused before
     * a procedure. Distinct from the MAR's per-dose Hold, which stops one dose
     * and leaves the order running.
     */
    suspend: (id: number, data: SuspendMedicationRequest) =>
      apiClient.post<MedicationResponse>(`/medications/${id}/suspend`, data, {
        __zodSchema: zod.zSuspendMedicationResponse,
      }),
    /**
     * Release a hold. An order that pharmacy never verified returns to
     * `pending`, not `active`: resuming clears the hold, not the pharmacy gate.
     */
    resume: (id: number) =>
      apiClient.post<MedicationResponse>(`/medications/${id}/resume`, {}, {
        __zodSchema: zod.zResumeMedicationResponse,
      }),
    /**
     * Proceed without pharmacist verification. Requires MED_VERIFY_OVERRIDE —
     * deliberately not held by a general staff nurse, since a bypass available
     * to whoever is holding the drug is not a control.
     */
    overrideVerification: (id: number, data: OverrideVerificationRequest) =>
      apiClient.post<MedicationResponse>(`/medications/${id}/override-verification`, data, {
        __zodSchema: zod.zOverrideVerificationResponse,
      }),
    /**
     * What the pharmacy encoded differently, per field. A hold that says only
     * "pharmacy disagrees" tells a prescriber nothing.
     */
    discrepancies: (id: number) =>
      apiClient.get<Discrepancy[]>(`/medications/${id}/discrepancies`, {
        __zodSchema: zod.zListDiscrepanciesResponse,
      }),
    /**
     * Settle a clarification and release the order. It returns to `pending`,
     * not `active`: pharmacy verifies whatever the order now says.
     */
    resolveClarification: (
      id: number,
      discrepancyId: number,
      data: ResolveClarificationRequest,
    ) =>
      apiClient.post<MedicationResponse>(
        `/medications/${id}/discrepancies/${discrepancyId}/resolve`,
        data,
        { __zodSchema: zod.zResolveClarificationResponse },
      ),
    delete: (id: number) =>
      apiClient.delete(`/medications/${id}`),
    administer: (medicationId: number, data: object) =>
      apiClient.post(`/medications/${medicationId}/administer`, data, {
        __zodSchema: zod.zRecordAdministrationResponse,
      }),
    // Records the independent second-nurse check on a high-alert order. The
    // optional body authenticates that second nurse inline — she is stamped as
    // the witness without a session being minted for her. Sent as `{}` when the
    // caller is recording a check made some other way; the endpoint rejects
    // unknown fields, so nothing else may ride along.
    doubleCheck: (medicationOrderId: number, witness?: { verifierUsername: string; verifierPassword: string }) =>
      apiClient.post(`/medications/${medicationOrderId}/double-check`, witness ?? {}),
    listAdministrations: (medicationId: number, params?: object) =>
      apiClient.get(`/medications/${medicationId}/administrations`, {
        params,
        __zodSchema: zod.zListAdministrationsResponse,
      }),
    /**
     * Pharmacy dispensing events for one order (`MedicationDispense`). Relay-only
     * server-side: a 503 means the data tier is unreachable, which a caller must
     * not render as "nothing has been dispensed".
     */
    listDispenses: (medicationOrderId: number, params?: object) =>
      apiClient.get<PaginatedResponse<MedicationDispenseResponse>>(
        `/medications/${medicationOrderId}/dispenses`,
        { params },
      ),
    /** Record an in-app dispense. Requires MED_DISPENSE. */
    recordDispense: (medicationOrderId: number, data: object) =>
      apiClient.post<MedicationDispenseResponse>(
        `/medications/${medicationOrderId}/dispenses`,
        data,
        { __zodSchema: zod.zRecordDispenseResponse },
      ),
    marSchedule: (params: { patientId: number; date: string }) =>
      apiClient.get('/medications/mar/schedule', {
        params,
        __zodSchema: zod.zGetMarScheduleResponse,
      }),
    calculateDose: (data: { medicationCatalogId: number; patientWeightKg: number; patientHeightCm?: number; patientGfr?: number; route?: string; indication?: string }) =>
      apiClient.post('/medications/calculate-dose', data),
  },

  // Documentation
  documentation: {
    list: (params?: { patientId?: number; encounterId?: number; noteType?: string; status?: string; unsigned?: boolean; page?: number; perPage?: number }) =>
      apiClient.get<PaginatedResponse<ClinicalNoteResponse>>('/notes', {
        params,
        __zodSchema: zod.zListNotesResponse,
      }),
    get: (id: number) =>
      apiClient.get<ClinicalNoteResponse>(`/notes/${id}`, {
        __zodSchema: zod.zGetNoteResponse,
      }),
    create: (data: CreateNoteRequest) =>
      apiClient.post<ClinicalNoteResponse>('/notes', data, {
        __zodSchema: zod.zCreateNoteResponse,
      }),
    update: (id: number, data: Partial<CreateNoteRequest>) =>
      apiClient.put<ClinicalNoteResponse>(`/notes/${id}`, data, {
        __zodSchema: zod.zUpdateNoteResponse,
      }),
    sign: (id: number, data: { signedByUserId: number }) =>
      apiClient.post(`/notes/${id}/sign`, data, {
        __zodSchema: zod.zSignNoteResponse,
      }),
    amend: (id: number, data: { amendmentReason: string; amendedByUserId: number }) =>
      apiClient.post(`/notes/${id}/amend`, data, {
        __zodSchema: zod.zAmendNoteResponse,
      }),
    cosign: (id: number, data: { cosignedByUserId: number }) =>
      apiClient.post(`/notes/${id}/cosign`, data, {
        __zodSchema: zod.zCosignNoteResponse,
      }),
    templates: {
      list: (params?: object) => apiClient.get('/notes/templates', {
        params,
        __zodSchema: zod.zListTemplatesResponse,
      }),
      get: (id: number) => apiClient.get(`/notes/templates/${id}`, {
        __zodSchema: zod.zGetTemplateResponse,
      }),
      create: (data: { templateName: string; templateType: string; specialty?: string; content: string; isActive?: boolean }) =>
        apiClient.post('/notes/templates', data, {
          __zodSchema: zod.zCreateTemplateResponse,
        }),
      update: (id: number, data: object) =>
        apiClient.put(`/notes/templates/${id}`, data, {
          __zodSchema: zod.zUpdateTemplateResponse,
        }),
    },
    smartPhrases: {
      list: (params?: object) => apiClient.get('/notes/smart-phrases', {
        params,
        __zodSchema: zod.zListSmartPhrasesResponse,
      }),
      create: (data: object) =>
        apiClient.post('/notes/smart-phrases', data, {
          __zodSchema: zod.zCreateSmartPhraseResponse,
        }),
      expand: (data: { phraseKey: string; patientId?: number; encounterId?: number }) =>
        apiClient.post('/notes/smart-phrases/expand', data, {
          __zodSchema: zod.zExpandSmartPhraseResponse,
        }),
    },
  },

  // Scheduling
  scheduling: {
    list: (params?: AppointmentSearchParams) =>
      apiClient.get('/scheduling/appointments', {
        params,
        __zodSchema: zod.zListAppointmentsResponse,
      }),
    get: (id: number) =>
      apiClient.get(`/scheduling/appointments/${id}`, {
        __zodSchema: zod.zGetAppointmentResponse,
      }),
    create: (data: CreateAppointmentRequest, options?: { suppressErrorToast?: boolean }) =>
      apiClient.post('/scheduling/appointments', data, {
        __zodSchema: zod.zCreateAppointmentResponse,
        __suppressErrorToast: options?.suppressErrorToast,
      }),
    update: (id: number, data: Partial<CreateAppointmentRequest>, options?: { suppressErrorToast?: boolean }) =>
      apiClient.put(`/scheduling/appointments/${id}`, data, {
        __zodSchema: zod.zUpdateAppointmentResponse,
        __suppressErrorToast: options?.suppressErrorToast,
      }),
    cancel: (id: number, reason: string) =>
      apiClient.post(`/scheduling/appointments/${id}/cancel`, { reason }, {
        __zodSchema: zod.zCancelAppointmentResponse,
      }),
    arrive: (id: number) =>
      apiClient.post(`/scheduling/appointments/${id}/arrive`, undefined, {
        __zodSchema: zod.zArriveResponse,
      }),
    checkIn: (id: number, data?: CheckInRequest) =>
      apiClient.post(`/scheduling/appointments/${id}/check-in`, data, {
        __zodSchema: zod.zCheckInAppointmentResponse,
      }),
    startVisit: (id: number) =>
      apiClient.post(`/scheduling/appointments/${id}/start-visit`, undefined, {
        __zodSchema: zod.zStartVisitResponse,
      }),
    getSlots: (params: { providerId: number; dateFrom: string; dateTo: string }) =>
      apiClient.get('/scheduling/slots', {
        params,
        __zodSchema: zod.zListAvailableSlotsResponse,
      }),
    waitlist: (params?: object) =>
      apiClient.get('/scheduling/waitlist', {
        params,
        __zodSchema: zod.zListWaitlistResponse,
      }),
    updateWaitlist: (
      id: number,
      data: { urgencyLevel?: string; waitlistStatus?: string },
    ) =>
      apiClient.patch(`/scheduling/waitlist/${id}`, data, {
        __zodSchema: zod.zUpdateWaitlistEntryResponse,
      }),
    templates: (params?: { providerId?: number }) =>
      apiClient.get('/scheduling/schedule-templates', {
        params,
        __zodSchema: zod.zListScheduleTemplatesResponse,
      }),
    createTemplate: (data: {
      providerId: number
      templateName: string
      facilityId?: number
      dayOfWeek: number
      startTime: string
      endTime: string
      appointmentDurationMinutes?: number
      effectiveStartDate: string
      effectiveEndDate?: string
    }) =>
      apiClient.post('/scheduling/schedule-templates', data, {
        __zodSchema: zod.zCreateScheduleTemplateResponse,
      }),
  },

  // Billing
  billing: {
    charges: {
      list: (params?: object) =>
        apiClient.get<PaginatedResponse<ChargeResponse>>('/billing/charges', {
          params,
          __zodSchema: zod.zListChargesResponse,
        }),
      create: (data: { encounterId: number; chargeCode: string; description: string; quantity: number; unitPrice: number; serviceDate: string }) =>
        apiClient.post<ChargeResponse>('/billing/charges', data, {
          __zodSchema: zod.zCreateChargeResponse,
        }),
    },
    claims: {
      list: (params?: object) =>
        apiClient.get<PaginatedResponse<ClaimResponse>>('/billing/claims', {
          params,
          __zodSchema: zod.zListClaimsResponse,
        }),
      create: (data: object) =>
        apiClient.post<ClaimResponse>('/billing/claims', data, {
          __zodSchema: zod.zCreateClaimResponse,
        }),
      submit: (id: number) =>
        apiClient.post<ClaimResponse>(`/billing/claims/${id}/submit`, undefined, {
          __zodSchema: zod.zSubmitClaimResponse,
        }),
    },
    payments: {
      list: (params?: object) =>
        apiClient.get<PaginatedResponse<PaymentResponse>>('/billing/payments', {
          params,
          __zodSchema: zod.zListPaymentsResponse,
        }),
      create: (data: { claimId?: number; patientId: number; paymentType: string; amount: number; paymentMethod: PaymentMethod; referenceNumber?: string; notes?: string }) =>
        apiClient.post<PaymentResponse>('/billing/payments', data, {
          __zodSchema: zod.zCreatePaymentResponse,
        }),
    },
    accounts: {
      get: (patientId: number) =>
        apiClient.get<PatientAccountResponse>(`/billing/accounts/${patientId}`, {
          __zodSchema: zod.zGetPatientAccountResponse,
        }),
    },
    statements: {
      generate: (data: object) =>
        apiClient.post('/billing/statements', data, {
          __zodSchema: zod.zGenerateStatementResponse,
        }),
    },
    denials: {
      list: (params?: object) =>
        apiClient.get('/billing/denials', {
          params,
          __zodSchema: zod.zListDenialsResponse,
        }),
      updateStatus: (id: number, data: DenialUpdateStatusRequest) =>
        apiClient.put<DenialUpdateStatusResponse>(`/billing/denials/${id}`, data, {
          __zodSchema: zod.zUpdateDenialStatusResponse,
        }),
    },
  },

  // Notifications
  notifications: {
    list: (params?: object) => apiClient.get('/notifications', { params }),
    send: (data: object) => apiClient.post('/notifications', data),
    markRead: (id: number) => apiClient.put(`/notifications/${id}/read`),
    markAllRead: () => apiClient.post('/notifications/read-all'),
    getPreferences: () => apiClient.get('/notifications/preferences'),
    updatePreference: (id: number, data: object) => apiClient.put(`/notifications/preferences/${id}`, data),
    // Escalation ladder admin (SYS_ADMIN_ALL). The notifications surface is not
    // in the OpenAPI spec, so there is no generated Zod schema to attach here.
    // There is deliberately no create/delete: only the `critical_result` trigger
    // has a consumer (the escalation sweeper), so a rule for any other trigger
    // would look configured and do nothing.
    listEscalationRules: (params?: { triggerEvent?: string; includeInactive?: boolean }) =>
      apiClient.get<EscalationRuleResponse[]>('/notifications/escalation-rules', { params }),
    updateEscalationRule: (id: number, data: UpdateEscalationRuleRequest) =>
      apiClient.put<EscalationRuleResponse>(`/notifications/escalation-rules/${id}`, data),
  },

  // Analytics
  analytics: {
    // S5-104: report computation is unbounded — give it 120s, not the
    // instance-default 30s, before surfacing a network failure.
    runReport: (data: object) => apiClient.post('/analytics/reports/run', data, { timeout: 120_000 }),
    listReports: () => apiClient.get('/analytics/reports'),
    dashboards: {
      list: () => apiClient.get('/analytics/dashboards'),
      get: (id: number) => apiClient.get(`/analytics/dashboards/${id}`),
      create: (data: object) => apiClient.post('/analytics/dashboards', data),
      update: (id: number, data: object) => apiClient.put(`/analytics/dashboards/${id}`, data),
      delete: (id: number) => apiClient.delete(`/analytics/dashboards/${id}`),
    },
    qualityMeasures: (params?: object) => apiClient.get('/analytics/quality-measures', { params }),
  },

  // Clinical Decision Support
  cds: {
    evaluate: (data: object) => apiClient.post('/cds/evaluate', data),
    checkInteractions: (data: object) => apiClient.post('/cds/drug-interactions', data),
    checkAllergies: (data: object) => apiClient.post('/cds/drug-allergies', data),
    alerts: {
      list: (params?: object) => apiClient.get('/cds/alerts', { params }),
      acknowledge: (id: number, userId: number) => apiClient.post(`/cds/alerts/${id}/acknowledge`, { acknowledgedByUserId: userId }),
      override: (id: number, data: object) => apiClient.post(`/cds/alerts/${id}/override`, data),
    },
    rules: {
      list: () => apiClient.get('/cds/rules'),
      create: (data: object) => apiClient.post('/cds/rules', data),
      update: (id: number, data: object) => apiClient.put(`/cds/rules/${id}`, data),
    },
  },

  // Terminology
  terminology: {
    search: (params: { system?: string; display?: string; limit?: number }) =>
      apiClient.get('/fhir/r4/CodeSystem/$search', { params }),
  },

  // FHIR R4
  //
  // FHIR methods are inherently dynamic — the same `getResource` call
  // can return a Patient, an Observation, or any of ~30 other resource
  // types depending on the `type` arg. The `__zodSchema` validation
  // pattern from the rest of api.ts is static per method (one method
  // ↔ one schema), so default-on wiring isn't possible here.
  //
  // Instead each FHIR method accepts an optional `config?:
  // AxiosRequestConfig` so callers can pass schema validation per call:
  //
  //   import { zPatient } from '@/types/zod/zod.gen';
  //   const { data } = await api.fhir.getResource('Patient', '123', {
  //     __zodSchema: zPatient,
  //   });
  //
  // The interceptor's keysToCamel step is already skipped for `/fhir/r4/*`
  // and `/fhir/r5/*` URLs via `isSpecDictatedUrl`, so the wire shape
  // (camelCase per FHIR spec) and the Zod schema (camelCase from the
  // utoipa-generated wire-format definitions) match end-to-end.
  fhir: {
    getResource: (type: string, id: string, config?: AxiosRequestConfig) =>
      apiClient.get(`/fhir/r4/${type}/${id}`, config),
    searchResources: (type: string, params?: Record<string, string>, config?: AxiosRequestConfig) =>
      apiClient.get(`/fhir/r4/${type}`, { params, ...config }),
    createResource: (type: string, data: object, config?: AxiosRequestConfig) =>
      apiClient.post(`/fhir/r4/${type}`, data, config),
    patientEverything: (id: string, config?: AxiosRequestConfig) =>
      apiClient.get(`/fhir/r4/Patient/${id}/$everything`, config),
    // FHIR Bulk Data kick-off. GET, not POST, and under `/r4` like every
    // sibling above — this was `POST /fhir/$export` with a JSON body, wrong on
    // both counts, so it would have 404'd and then 405'd the moment anything
    // called it. Nothing did, which is why it survived.
    //
    // The spec passes its arguments as QUERY parameters (`_type`, `_since`,
    // `_outputFormat`, `includeAssociatedData` — the casing is the spec's, and
    // the gateway's `Query<ExportKickoffParams>` binds those exact names).
    // Kick-off replies 202 with a `Content-Location` header to poll.
    bulkExport: (params?: ExportKickoffParams, config?: AxiosRequestConfig) =>
      apiClient.get('/fhir/r4/$export', { params, ...config }),
  },

  // Patient Portal — public endpoints use apiClient, authenticated use portalClient
  portal: {
    login: (data: { email: string; password: string }) =>
      apiClient.post('/portal/login', data, {
        __zodSchema: zod.zPortalLoginResponse2,
      }),
    register: (data: { email: string; password: string; fullName: string; dateOfBirth: string; phone?: string; mrn?: string; nationalIdType?: string; nationalIdNumber?: string }) =>
      apiClient.post('/portal/register', data, {
        __zodSchema: zod.zPortalRegisterResponse,
      }),
    getUser: () =>
      portalClient.get('/portal/user', {
        __zodSchema: zod.zGetPortalUserResponse,
      }),
    updateUser: (data: { fullName?: string; phone?: string; preferredLanguage?: string }) =>
      portalClient.put('/portal/user', data, {
        __zodSchema: zod.zUpdatePortalUserResponse,
      }),
    messages: {
      list: (params?: object) =>
        portalClient.get('/portal/messages', {
          params,
          __zodSchema: zod.zListPortalMessagesResponse,
        }),
      send: (data: { subject: string; body: string; threadId?: number; recipientProviderId?: number; recipientDepartmentId?: number; category?: string; priority?: string }) =>
        portalClient.post('/portal/messages', data, {
          __zodSchema: zod.zSendMessageResponse,
        }),
    },
    summary: () =>
      portalClient.get('/portal/summary', {
        __zodSchema: zod.zGetPatientSummaryResponse,
      }),
    appointments: (params?: object) =>
      portalClient.get('/portal/appointments', {
        params,
        __zodSchema: zod.zListPortalAppointmentsResponse,
      }),
    results: (params?: object) =>
      portalClient.get('/portal/results', {
        params,
        __zodSchema: zod.zListPortalResultsResponse,
      }),
    forms: {
      list: () =>
        portalClient.get('/portal/forms', {
          __zodSchema: zod.zListFormsResponse,
        }),
      submit: (data: { formId: number; responses: Record<string, unknown> }) =>
        portalClient.post('/portal/forms', data, {
          __zodSchema: zod.zSubmitFormResponse,
        }),
    },
  },

  // Providers
  providers: {
    list: (params?: object) => apiClient.get('/providers', { params }),
    get: (id: number) => apiClient.get(`/providers/${id}`),
  },

  // Configuration
  config: {
    list: () => apiClient.get('/config').then(r => r.data),
    get: (key: string) => apiClient.get(`/config/${key}`).then(r => r.data),
    update: (key: string, value: string) => apiClient.put(`/config/${key}`, { value }).then(r => r.data),
    getFacilityOverrides: (facilityId: number) => apiClient.get(`/config/facility/${facilityId}`).then(r => r.data),
    setFacilityOverride: (facilityId: number, key: string, value: string) =>
        apiClient.put(`/config/facility/${facilityId}/${key}`, { value }).then(r => r.data),
    deleteFacilityOverride: (facilityId: number, key: string) =>
        apiClient.delete(`/config/facility/${facilityId}/${key}`).then(r => r.data),
    getUserPreferences: () => apiClient.get('/config/user/preferences').then(r => r.data),
    setUserPreference: (key: string, value: string) =>
        apiClient.put(`/config/user/preferences/${key}`, { value }).then(r => r.data),
  },

  // Break-Glass Access
  breakGlass: {
    // R-2.8: exactly one of password / totpCode. Password-holding accounts
    // re-enter the password; SSO-only accounts (no local password) step up
    // with a TOTP code from their enrolled authenticator.
    create: (data: { patientId: number; justification: string; password?: string; totpCode?: string }) =>
      apiClient.post('/break-glass', data, {
        __zodSchema: zod.zCreateBreakGlassResponse,
      }),
  },

  // RBAC
  rbac: {
    // Categories
    listCategories: () => apiClient.get('/rbac/categories', { __zodSchema: zod.zListCategoriesResponse }).then(r => r.data),
    createCategory: (data: object) => apiClient.post('/rbac/categories', data, { __zodSchema: zod.zCreateCategoryResponse }).then(r => r.data),
    getCategory: (id: number) => apiClient.get(`/rbac/categories/${id}`, { __zodSchema: zod.zGetCategoryResponse }).then(r => r.data),
    updateCategory: (id: number, data: object) => apiClient.put(`/rbac/categories/${id}`, data, { __zodSchema: zod.zUpdateCategoryResponse }).then(r => r.data),
    deleteCategory: (id: number) => apiClient.delete(`/rbac/categories/${id}`),
    getCategoryPermissions: (id: number) => apiClient.get(`/rbac/categories/${id}/permissions`, { __zodSchema: zod.zListCategoryPermissionsResponse }).then(r => r.data),
    setCategoryPermissions: (id: number, permissionIds: number[]) => apiClient.put(`/rbac/categories/${id}/permissions`, { permissionIds }, { __zodSchema: zod.zSetCategoryPermissionsResponse }).then(r => r.data),

    // Roles
    listRoles: () => apiClient.get('/rbac/roles', { __zodSchema: zod.zListRolesResponse }).then(r => r.data),
    createRole: (data: object) => apiClient.post('/rbac/roles', data, { __zodSchema: zod.zCreateRoleResponse }).then(r => r.data),
    getRole: (id: number) => apiClient.get(`/rbac/roles/${id}`, { __zodSchema: zod.zGetRoleResponse }).then(r => r.data),
    updateRole: (id: number, data: object) => apiClient.put(`/rbac/roles/${id}`, data, { __zodSchema: zod.zUpdateRoleResponse }).then(r => r.data),
    deleteRole: (id: number) => apiClient.delete(`/rbac/roles/${id}`),
    getRolePermissions: (id: number) => apiClient.get(`/rbac/roles/${id}/permissions`, { __zodSchema: zod.zListRolePermissionsResponse }).then(r => r.data),
    setRolePermissions: (id: number, permissionIds: number[]) => apiClient.put(`/rbac/roles/${id}/permissions`, { permissionIds }, { __zodSchema: zod.zSetRolePermissionsResponse }).then(r => r.data),
    getEffectivePermissions: (id: number) => apiClient.get(`/rbac/roles/${id}/effective`, { __zodSchema: zod.zGetEffectivePermissionsResponse }).then(r => r.data),
    getDeniedPermissions: (id: number) => apiClient.get(`/rbac/roles/${id}/denied`, { __zodSchema: zod.zListDeniedPermissionsResponse }).then(r => r.data),
    setDeniedPermissions: (id: number, items: object[]) => apiClient.put(`/rbac/roles/${id}/denied`, { denied: items }, { __zodSchema: zod.zSetDeniedPermissionsResponse }).then(r => r.data),

    // Permissions
    listPermissions: () => apiClient.get('/rbac/permissions', { __zodSchema: zod.zListPermissionsResponse }).then(r => r.data),
    createPermission: (data: object) => apiClient.post('/rbac/permissions', data, { __zodSchema: zod.zCreatePermissionResponse }).then(r => r.data),
    updatePermission: (id: number, data: object) => apiClient.put(`/rbac/permissions/${id}`, data, { __zodSchema: zod.zUpdatePermissionResponse }).then(r => r.data),
    deletePermission: (id: number) => apiClient.delete(`/rbac/permissions/${id}`),

    // Matrix
    getMatrix: () => apiClient.get('/rbac/matrix', { __zodSchema: zod.zGetAccessMatrixResponse }).then(r => r.data),

    // User roles
    getUserRoles: (userId: number): Promise<UserRoleAssignment[]> => apiClient.get(`/rbac/users/${userId}/roles`, { __zodSchema: zod.zListUserRolesResponse }).then(r => r.data),
    assignUserRole: (userId: number, data: object) => apiClient.post(`/rbac/users/${userId}/roles`, data, { __zodSchema: zod.zAssignUserRoleResponse }).then(r => r.data),
    removeUserRole: (userId: number, roleId: number) => apiClient.delete(`/rbac/users/${userId}/roles/${roleId}`),
    // B1 re-date. Ad-hoc `{updated: true}` body like its DELETE sibling —
    // no zod response schema (see removeUserRole above).
    updateUserRole: (userId: number, roleId: number, data: UpdateUserRoleRequest) => apiClient.put(`/rbac/users/${userId}/roles/${roleId}`, data).then(r => r.data),
    // Access reviews (C).
    listReviewCampaigns: (): Promise<ReviewCampaign[]> => apiClient.get('/rbac/reviews', { __zodSchema: zod.zListCampaignsResponse }).then(r => r.data),
    createReviewCampaign: (data: CreateReviewCampaignRequest): Promise<ReviewCampaign> => apiClient.post('/rbac/reviews', data, { __zodSchema: zod.zCreateCampaignResponse }).then(r => r.data),
    getReviewCampaign: (campaignId: number): Promise<ReviewCampaignDetail> => apiClient.get(`/rbac/reviews/${campaignId}`, { __zodSchema: zod.zGetCampaignResponse }).then(r => r.data),
    certifyReviewItem: (campaignId: number, itemId: number): Promise<ReviewItem> => apiClient.post(`/rbac/reviews/${campaignId}/items/${itemId}/certify`, {}, { __zodSchema: zod.zCertifyItemResponse }).then(r => r.data),
    revokeReviewItem: (campaignId: number, itemId: number): Promise<ReviewItem> => apiClient.post(`/rbac/reviews/${campaignId}/items/${itemId}/revoke`, {}, { __zodSchema: zod.zRevokeItemResponse }).then(r => r.data),
    closeReviewCampaign: (campaignId: number): Promise<ReviewCampaign> => apiClient.post(`/rbac/reviews/${campaignId}/close`, {}, { __zodSchema: zod.zCloseCampaignResponse }).then(r => r.data),
    deleteReviewCampaign: (campaignId: number) => apiClient.delete(`/rbac/reviews/${campaignId}`),
    getUserPermissions: (userId: number) => apiClient.get(`/rbac/users/${userId}/permissions`, { __zodSchema: zod.zGetUserPermissionsResponse }).then(r => r.data),
    // Login departments (F3): which department(s) an account works in.
    getUserDepartments: (userId: number): Promise<UserDepartmentAssignment[]> => apiClient.get(`/rbac/users/${userId}/departments`, { __zodSchema: zod.zListUserDepartmentsResponse }).then(r => r.data),
    setUserDepartments: (userId: number, data: SetUserDepartmentsRequest): Promise<UserDepartmentAssignment[]> => apiClient.put(`/rbac/users/${userId}/departments`, data, { __zodSchema: zod.zSetUserDepartmentsResponse }).then(r => r.data),
    // User groups (S6a): HS-scoped groups with materialized grants.
    listGroups: (): Promise<UserGroup[]> => apiClient.get('/rbac/groups', { __zodSchema: zod.zListGroupsResponse }).then(r => r.data),
    getGroup: (id: number): Promise<UserGroupDetail> => apiClient.get(`/rbac/groups/${id}`, { __zodSchema: zod.zGetGroupResponse }).then(r => r.data),
    createGroup: (data: CreateUserGroupInput): Promise<UserGroup> => apiClient.post('/rbac/groups', data, { __zodSchema: zod.zCreateGroupResponse }).then(r => r.data),
    updateGroup: (id: number, data: UpdateUserGroupInput): Promise<UserGroup> => apiClient.put(`/rbac/groups/${id}`, data, { __zodSchema: zod.zUpdateGroupResponse }).then(r => r.data),
    deleteGroup: (id: number): Promise<UserGroupDeleteResult> => apiClient.delete(`/rbac/groups/${id}`, { __zodSchema: zod.zDeleteGroupResponse }).then(r => r.data),
    addGroupMember: (id: number, data: AddGroupMemberInput): Promise<UserGroupMember> => apiClient.post(`/rbac/groups/${id}/members`, data, { __zodSchema: zod.zAddGroupMemberResponse }).then(r => r.data),
    removeGroupMember: (id: number, userId: number) => apiClient.delete(`/rbac/groups/${id}/members/${userId}`),
    addGroupGrant: (id: number, data: AddGroupGrantInput): Promise<UserGroupGrant> => apiClient.post(`/rbac/groups/${id}/grants`, data, { __zodSchema: zod.zAddGroupGrantResponse }).then(r => r.data),
    removeGroupGrant: (id: number, grantId: number) => apiClient.delete(`/rbac/groups/${id}/grants/${grantId}`),
  },

  // Admin / User & Role Management (delegates to /rbac/ endpoints)
  admin: {
    users: {
      list: (params?: object) => apiClient.get('/rbac/users', {
        params,
        __zodSchema: zod.zListUsersResponse,
      }),
      get: (id: number) => apiClient.get(`/rbac/users/${id}`),
      create: (data: object) => apiClient.post('/rbac/users', data),
      update: (id: number, data: object) => apiClient.put(`/rbac/users/${id}`, data),
      delete: (id: number) => apiClient.delete(`/rbac/users/${id}`),
    },
    roles: {
      list: () => apiClient.get('/rbac/roles'),
      create: (data: object) => apiClient.post('/rbac/roles', data),
      update: (id: number, data: object) => apiClient.put(`/rbac/roles/${id}`, data),
    },
  },

  // Liveness/readiness probes. Raw (no Zod): the readiness body carries
  // free-form per-component error details, and callers only read `status`.
  // Same catch-null precedent as AdminSystem's /health read.
  system: {
    readiness: () =>
      apiClient
        .get<{
          status: string
          version: string
          service: string
          components?: Record<string, unknown>
          timestamp: string
        }>('/health/ready')
        .then(r => r.data)
        .catch(() => null),
  },

  // Protocols — backend mounts clinical_workflows under /clinical (not
  // /clinical-workflows); enroll takes the protocol id in the path.
  protocols: {
    list: () => apiClient.get('/clinical/protocols'),
    enroll: (data: { protocolId: number; encounterId: number; patientId: number }) =>
      apiClient.post(`/clinical/protocols/${data.protocolId}/enroll`, data),
    enrollments: (params?: object) =>
      apiClient.get('/clinical/protocols/enrollments', { params }),
    advanceStep: (id: number) =>
      apiClient.post(`/clinical/protocols/enrollments/${id}/advance`),
  },

  // R-1.1: Handoff / I-PASS
  handoffs: {
    listPending: () =>
      apiClient.get<HandoffResponse[]>('/clinical/handoffs'),
    get: (id: number) =>
      apiClient.get<HandoffResponse>(`/clinical/handoffs/${id}`),
    create: (data: CreateHandoffRequest) =>
      apiClient.post<HandoffResponse>('/clinical/handoffs', data),
    acknowledge: (id: number, data: { synthesis?: string }) =>
      apiClient.post(`/clinical/handoffs/${id}/acknowledge`, data),
  },

  // R-1.2: Nursing task queue
  nursing: {
    worklist: () =>
      apiClient.get<WorklistSummary>('/clinical/nursing/worklist'),
    createTask: (data: CreateNursingTaskRequest) =>
      apiClient.post('/clinical/nursing/tasks', data),
    completeTask: (id: number) =>
      apiClient.post(`/clinical/nursing/tasks/${id}/complete`),
  },

  // R-1.3: Specimen tracking
  specimens: {
    list: (patientId: number) =>
      apiClient.get<SpecimenResponse[]>('/clinical/specimens', {
        params: { patientId },
      }),
    get: (id: number) =>
      apiClient.get<SpecimenResponse>(`/clinical/specimens/${id}`),
    create: (data: CreateSpecimenRequest) =>
      apiClient.post<SpecimenResponse>('/clinical/specimens', data),
    updateStatus: (id: number, data: UpdateSpecimenStatusRequest) =>
      apiClient.put(`/clinical/specimens/${id}/status`, data),
    track: (barcode: string) =>
      apiClient.get<SpecimenTrackResponse>(`/clinical/specimens/track/${barcode}`),
  },

  // R-1.4: Discharge checklist
  discharge: {
    getChecklist: (encounterId: number) =>
      apiClient.get<DischargeChecklistResponse>(`/clinical/discharge/${encounterId}/checklist`),
    completeStep: (encounterId: number, step: string) =>
      apiClient.post(`/clinical/discharge/${encounterId}/checklist/${step}`),
  },

  // R-1.5: Order sets + smart phrases
  orderSets: {
    // Paginated as of 0.99.0: the endpoint used to end in a fixed LIMIT 100
    // with no total, so a catalogue past a hundred sets was truncated without
    // the caller ever being told. Omitting the params keeps the historical
    // window of 100 and adds the total.
    list: (params?: {
      category?: string
      specialty?: string
      q?: string
      page?: number
      perPage?: number
    }) =>
      apiClient.get<PaginatedResponse<OrderSetResponse>>('/clinical/order-sets', { params }),
    get: (id: number) =>
      apiClient.get<OrderSetResponse>(`/clinical/order-sets/${id}`),
    getItems: (id: number) =>
      apiClient.get<OrderSetItemResponse[]>(`/clinical/order-sets/${id}/items`),
  },

  smartPhrases: {
    // Paginated as of 0.99.0 — see orderSets.list above.
    list: (params?: { category?: string; q?: string; page?: number; perPage?: number }) =>
      apiClient.get<PaginatedResponse<OrderSmartPhraseResponse>>('/clinical/smart-phrases', {
        params,
      }),
    expand: (code: string) =>
      apiClient.get<OrderSmartPhraseResponse>(`/clinical/smart-phrases/${code}`),
  },

  // R-0.5: Pending cosign queue
  cosign: {
    pendingOrders: () =>
      apiClient.get<PendingCosignOrder[]>('/orders/pending-cosign'),
  },

  // CCOW context management
  context: {
    get: () => apiClient.get('/context/get'),
    set: (data: { patientId?: number; encounterId?: number; facilityId?: number }) =>
      apiClient.post('/context/set', data),
    join: (data: { applicationName: string; callbackUrl?: string }) =>
      apiClient.post('/context/join', data),
    leave: (data: { applicationName: string }) =>
      apiClient.post('/context/leave', data),
    survey: (data: { patientId?: number; encounterId?: number; facilityId?: number }) =>
      apiClient.post('/context/survey', data),
    commit: () => apiClient.post('/context/commit'),
    cancel: () => apiClient.post('/context/cancel'),
  },

  // Third-party app configuration + launch
  thirdPartyApps: {
    list: (params?: { displayInChart?: boolean; displayInSidebar?: boolean; appCategory?: string; isActive?: boolean }) =>
      apiClient.get<ThirdPartyAppResponse[]>('/third-party-apps', {
        params,
        __zodSchema: zod.zListAppsResponse,
      }),
    get: (id: number) =>
      apiClient.get<ThirdPartyAppResponse>(`/third-party-apps/${id}`, {
        __zodSchema: zod.zGetAppResponse,
      }),
    create: (data: CreateThirdPartyAppRequest) =>
      apiClient.post<ThirdPartyAppResponse>('/third-party-apps', data, {
        __zodSchema: zod.zCreateAppResponse,
      }),
    update: (id: number, data: UpdateThirdPartyAppRequest) =>
      apiClient.put<ThirdPartyAppResponse>(`/third-party-apps/${id}`, data, {
        __zodSchema: zod.zUpdateAppResponse,
      }),
    deactivate: (id: number) =>
      apiClient.delete(`/third-party-apps/${id}`),
    launch: (appCode: string, data: ThirdPartyLaunchRequest) =>
      apiClient.post<ThirdPartyLaunchResponse>(`/third-party-apps/${appCode}/launch`, data, {
        __zodSchema: zod.zLaunchAppResponse,
      }),
  },

  // R-3.4 + R-3.5: Admin health
  adminHealth: {
    deadLetters: () =>
      apiClient.get<DeadLetterEntry[]>('/admin/outbox/dead-letter', {
        __zodSchema: zod.zListDeadLetterResponse,
      }),
    requeueDeadLetter: (id: number) =>
      apiClient.post(`/admin/outbox/dead-letter/${id}/requeue`),
    projectorLag: () =>
      apiClient.get<ProjectorLagResponse[]>('/admin/health/projector-lag', {
        __zodSchema: zod.zProjectorLagResponse2,
      }),
  },

  // HL7 interface message log + operator actions. Mirrors
  // backend/gateway/src/api/interface_messages.rs.
  interfaces: {
    stats: () =>
      apiClient.get<IntegrationStats[]>('/admin/interfaces/stats', {
        __zodSchema: zod.zIntegrationStatsResponse,
      }),
    list: (filters: InterfaceMessageFilters = {}) =>
      apiClient.get<InterfaceMessageListResponse>('/admin/interfaces/messages', {
        params: filters,
        __zodSchema: zod.zListInterfaceMessagesResponse,
      }),
    detail: (id: number, direction?: 'in' | 'out') =>
      // `direction` disambiguates the id — inbound (HL7Message) and outbound
      // (OutboundHL7Queue) are separate, overlapping id-spaces, so omitting it
      // can load the wrong message's decrypted PHI.
      apiClient.get<InterfaceMessageDetail>(`/admin/interfaces/messages/${id}`, {
        params: direction ? { direction } : undefined,
        __zodSchema: zod.zMessageDetailResponse,
      }),
    attempts: (id: number) =>
      apiClient.get<InterfaceMessageAttempt[]>(
        `/admin/interfaces/messages/${id}/attempts`,
        {
          __zodSchema: zod.zMessageAttemptsResponse,
        },
      ),
    // retry/resend require a body: the backend handlers extract
    // `Json<ReasonBody>` and `validate_reason` rejects whitespace-only
    // / empty / >1000 chars. Pre-sweep these methods didn't send a
    // body and the requests would 400 in production. Default reason
    // strings keep the audit trail honest until UI surfaces add real
    // reason textboxes (mirroring the skip/abandon pattern).
    retry: (id: number, reason: string = 'Operator-initiated retry') =>
      apiClient.post(`/admin/interfaces/messages/${id}/retry`, { reason }),
    skip: (id: number, reason: string) =>
      apiClient.post(`/admin/interfaces/messages/${id}/skip`, { reason }),
    abandon: (id: number, reason: string) =>
      apiClient.post(`/admin/interfaces/messages/${id}/abandon`, { reason }),
    resend: (id: number, reason: string = 'Operator-initiated resend') =>
      apiClient.post(`/admin/interfaces/messages/${id}/resend`, { reason }),
    timeseries: (integrationId: number, params: TimeseriesParams = {}) =>
      apiClient.get<TimeseriesResponse>(
        `/admin/interfaces/integrations/${integrationId}/timeseries`,
        {
          params,
          __zodSchema: zod.zIntegrationTimeseriesResponse,
        },
      ),
  },

  // Single sign-on (S4): public login legs + USER_MANAGE-gated admin CRUD.
  // Lists validate against the model schema's `.array()` — the spec
  // returns bare arrays, not named list envelopes.
  sso: {
    // -- public login legs (no session) --
    listPublicProviders: () =>
      apiClient.get<SsoProviderInfo[]>('/auth/sso/providers', {
        __zodSchema: zod.zSsoProviderInfo.array(),
      }),
    discover: (email: string) =>
      apiClient.get<SsoProviderInfo>('/auth/sso/discover', {
        params: { email },
        __zodSchema: zod.zSsoProviderInfo,
      }),
    listPublicDirectories: () =>
      apiClient.get<LdapDirectoryInfo[]>('/auth/ldap/directories', {
        __zodSchema: zod.zLdapDirectoryInfo.array(),
      }),
    callback: (code: string, state: string) =>
      apiClient.get<SsoTokenPair>('/auth/sso/callback', {
        params: { code, state },
        __zodSchema: zod.zTokenPair,
      }),
    // S5: exchange the single-use SAML artifact the ACS minted for a session.
    exchangeSamlArtifact: (artifact: string) =>
      apiClient.post<SsoTokenPair>('/auth/sso/saml/exchange', { artifact }, {
        __zodSchema: zod.zTokenPair,
      }),
    // -- providers --
    listProviders: () =>
      apiClient.get<SsoProvider[]>('/admin/sso/providers', {
        __zodSchema: zod.zSsoProviderResponse.array(),
      }),
    getProvider: (id: number) =>
      apiClient.get<SsoProvider>(`/admin/sso/providers/${id}`, {
        __zodSchema: zod.zSsoProviderResponse,
      }),
    createProvider: (data: CreateSsoProviderInput) =>
      apiClient.post<SsoProvider>('/admin/sso/providers', data, {
        __zodSchema: zod.zSsoProviderResponse,
      }),
    updateProvider: (id: number, data: UpdateSsoProviderInput) =>
      apiClient.put<SsoProvider>(`/admin/sso/providers/${id}`, data, {
        __zodSchema: zod.zSsoProviderResponse,
      }),
    deleteProvider: (id: number) =>
      apiClient.delete<SsoDeleteKept>(`/admin/sso/providers/${id}`),
    testProvider: (id: number) =>
      apiClient.post<SsoTestResult>(`/admin/sso/providers/${id}/test`, undefined, {
        __zodSchema: zod.zSsoTestResult,
      }),
    // -- domains --
    listDomains: () =>
      apiClient.get<SsoDomain[]>('/admin/sso/domains', {
        __zodSchema: zod.zSsoDomainResponse.array(),
      }),
    createDomain: (data: CreateSsoDomainInput) =>
      apiClient.post<SsoDomain>('/admin/sso/domains', data, {
        __zodSchema: zod.zSsoDomainResponse,
      }),
    deleteDomain: (domain: string) =>
      apiClient.delete(`/admin/sso/domains/${encodeURIComponent(domain)}`),
    // -- OIDC group maps --
    listGroupMaps: () =>
      apiClient.get<SsoGroupMap[]>('/admin/sso/group-maps', {
        __zodSchema: zod.zSsoGroupMapResponse.array(),
      }),
    createGroupMap: (data: CreateSsoGroupMapInput) =>
      apiClient.post<SsoGroupMap>('/admin/sso/group-maps', data, {
        __zodSchema: zod.zSsoGroupMapResponse,
      }),
    deleteGroupMap: (id: number) => apiClient.delete(`/admin/sso/group-maps/${id}`),
    // -- directories --
    listDirectories: () =>
      apiClient.get<SsoDirectory[]>('/admin/sso/directories', {
        __zodSchema: zod.zSsoDirectoryResponse.array(),
      }),
    getDirectory: (id: number) =>
      apiClient.get<SsoDirectory>(`/admin/sso/directories/${id}`, {
        __zodSchema: zod.zSsoDirectoryResponse,
      }),
    createDirectory: (data: CreateSsoDirectoryInput) =>
      apiClient.post<SsoDirectory>('/admin/sso/directories', data, {
        __zodSchema: zod.zSsoDirectoryResponse,
      }),
    updateDirectory: (id: number, data: UpdateSsoDirectoryInput) =>
      apiClient.put<SsoDirectory>(`/admin/sso/directories/${id}`, data, {
        __zodSchema: zod.zSsoDirectoryResponse,
      }),
    deleteDirectory: (id: number) =>
      apiClient.delete<SsoDeleteKept>(`/admin/sso/directories/${id}`),
    // -- LDAP maps --
    listLdapMaps: () =>
      apiClient.get<SsoLdapMap[]>('/admin/sso/ldap-maps', {
        __zodSchema: zod.zSsoLdapMapResponse.array(),
      }),
    createLdapMap: (data: CreateSsoLdapMapInput) =>
      apiClient.post<SsoLdapMap>('/admin/sso/ldap-maps', data, {
        __zodSchema: zod.zSsoLdapMapResponse,
      }),
    deleteLdapMap: (id: number) => apiClient.delete(`/admin/sso/ldap-maps/${id}`),
    // -- SCIM tokens (S6b) --
    listScimTokens: () =>
      apiClient.get<ScimToken[]>('/admin/sso/scim/tokens', {
        __zodSchema: zod.zScimTokenResponse.array(),
      }),
    mintScimToken: (data: MintScimTokenInput) =>
      apiClient.post<MintedScimToken>('/admin/sso/scim/tokens', data, {
        __zodSchema: zod.zMintedScimTokenResponse,
      }),
    revokeScimToken: (id: number) => apiClient.delete(`/admin/sso/scim/tokens/${id}`),
  },
}

export default api
